// src/components/MealPlanner.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../App";
import { getSavedMeals } from "../services/firebase";
import { generateWeeklyPlan } from "../utils/mealPlanGenerator";

const SLOT_LABELS = {
  breakfast: { label: "Breakfast", icon: "🌅" },
  lunch:     { label: "Lunch",     icon: "☀️"  },
  dinner:    { label: "Dinner",    icon: "🌙"  },
  snack:     { label: "Snack",     icon: "🍎"  },
};

const TAG_COLORS = {
  "quick":        "badge-green",
  "batch-cook":   "badge-blue",
  "high-protein": "badge-orange",
  "meal-prep":    "badge-yellow",
  "no-cook":      "badge-green",
  "low-carb":     "badge-yellow",
  "low-fat":      "badge-blue",
  "saved":        "badge-orange",
};

export default function MealPlanner() {
  const { user, profile, lockedMeals, setLockedMeals } = useApp();
  const navigate = useNavigate();

  const [plan,         setPlan]         = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [expandedDay,  setExpandedDay]  = useState(null);
  const [expandedMeal, setExpandedMeal] = useState(null); // "dayIndex_slot"

  const generate = useCallback(async (locked = lockedMeals) => {
    if (!profile) return;
    setLoading(true);
    const { data: saved } = await getSavedMeals(user.uid);
    const newPlan = generateWeeklyPlan({
      calorie_target: profile.calorie_target,
      protein_target: profile.protein_target,
      lockedMeals:    locked,
      customMeals:    saved || [],
    });
    setPlan(newPlan);
    setLoading(false);
  }, [profile, user, lockedMeals]);

  useEffect(() => { generate([]); }, [profile, generate]);

  const toggleLock = (dayIndex, slot, snackIndex = 0) => {
    setLockedMeals((prev) => {
      const isLocked = prev.some(
        (l) => l.dayIndex === dayIndex && l.slot === slot && (l.snackIndex ?? 0) === snackIndex
      );
      if (isLocked) {
        return prev.filter(
          (l) => !(l.dayIndex === dayIndex && l.slot === slot && (l.snackIndex ?? 0) === snackIndex)
        );
      } else {
        const meal = slot === "snack"
          ? plan[dayIndex].snacks[snackIndex]
          : plan[dayIndex][slot];
        return [...prev, { dayIndex, slot, snackIndex, meal }];
      }
    });
  };

  const isLocked = (dayIndex, slot, snackIndex = 0) =>
    lockedMeals.some(
      (l) => l.dayIndex === dayIndex && l.slot === slot && (l.snackIndex ?? 0) === snackIndex
    );

  const handleRegenerate = () => generate(lockedMeals);

  const toggleMeal = (key) =>
    setExpandedMeal((prev) => (prev === key ? null : key));

  // Day totals
  const dayTotals = (day) => {
    const meals = [day.breakfast, day.lunch, day.dinner, ...day.snacks].filter(Boolean);
    return meals.reduce(
      (acc, m) => ({
        calories: acc.calories + (m.calories || 0),
        protein:  acc.protein  + (m.protein  || 0),
        carbs:    acc.carbs    + (m.carbs    || 0),
        fat:      acc.fat      + (m.fat      || 0),
      }),
      { calories: 0, protein: 0, carbs: 0, fat: 0 }
    );
  };

  return (
    <div className="space-y-5 animate-slide-up">
      {/* ── Header ────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="section-title">Meal Planner</h1>
          {profile && (
            <p className="text-slate-500 text-sm mt-0.5">
              Built around {profile.calorie_target.toLocaleString()} cal · {profile.protein_target}g protein/day
            </p>
          )}
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button onClick={handleRegenerate} disabled={loading}
            className="btn-ghost text-sm flex items-center gap-1.5">
            <RefreshIcon className="w-4 h-4" />
            Regenerate
          </button>
          <button onClick={() => navigate("/grocery")}
            className="btn-primary text-sm flex items-center gap-1.5">
            <BagIcon className="w-4 h-4" />
            Grocery
          </button>
        </div>
      </div>

      {/* ── Lock hint ─────────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 text-xs text-slate-500 bg-[#111820] border border-[#1f2a3c] rounded-lg px-3 py-2">
        <LockIcon className="w-3.5 h-3.5 text-[#ff7c0a]" />
        <span>Lock meals you like — they'll stay when you regenerate and populate your grocery list.</span>
      </div>

      {/* ── Plan ──────────────────────────────────────────────────────── */}
      {loading ? (
        <PlanSkeleton />
      ) : !plan ? (
        <EmptyState />
      ) : (
        <div className="space-y-3">
          {plan.map((day) => {
            const totals = dayTotals(day);
            const isOpen = expandedDay === day.dayIndex;

            return (
              <div key={day.dayIndex} className="card overflow-hidden">
                {/* Day header — always visible */}
                <button
                  onClick={() => setExpandedDay(isOpen ? null : day.dayIndex)}
                  className="w-full flex items-center justify-between text-left"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm
                                    ${isOpen ? "bg-[#ff7c0a] text-white" : "bg-[#1f2a3c] text-slate-400"}`}>
                      {day.dayIndex + 1}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-slate-200">{day.dayName}</div>
                      <div className="text-xs text-slate-500">
                        {totals.calories} cal · {totals.protein}g protein
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {/* Protein hit indicator */}
                    {profile && totals.protein >= profile.protein_target && (
                      <span className="text-[10px] text-emerald-400 font-semibold">✓ Protein</span>
                    )}
                    <ChevronIcon className={`w-4 h-4 text-slate-500 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </div>
                </button>

                {/* Expanded day meals */}
                {isOpen && (
                  <div className="mt-4 space-y-2 border-t border-[#1f2a3c] pt-4">
                    {[
                      { slot: "breakfast", meal: day.breakfast },
                      { slot: "lunch",     meal: day.lunch     },
                      { slot: "dinner",    meal: day.dinner    },
                      ...(day.snacks || []).map((m, i) => ({ slot: "snack", snackIndex: i, meal: m })),
                    ].map(({ slot, snackIndex = 0, meal }) => {
                      if (!meal) return null;
                      const mealKey  = `${day.dayIndex}_${slot}_${snackIndex}`;
                      const locked   = isLocked(day.dayIndex, slot, snackIndex);
                      const expanded = expandedMeal === mealKey;
                      const meta     = SLOT_LABELS[slot];

                      return (
                        <div key={mealKey}
                          className={`rounded-lg border transition-all ${
                            locked
                              ? "border-[#ff7c0a]/40 bg-[#ff7c0a]/5"
                              : "border-[#1f2a3c] bg-[#111820]"
                          }`}>
                          {/* Meal row */}
                          <div className="flex items-center gap-3 px-3 py-2.5">
                            <span className="text-base flex-shrink-0">{meta.icon}</span>
                            <button
                              onClick={() => toggleMeal(mealKey)}
                              className="flex-1 text-left min-w-0"
                            >
                              <div className="text-sm font-medium text-slate-200 truncate">
                                {meal.name}
                              </div>
                              <div className="text-xs text-slate-500 mt-0.5">
                                {meal.calories} cal ·{" "}
                                <span className="text-[#ff7c0a]">{meal.protein}g P</span> ·{" "}
                                {meal.carbs}g C · {meal.fat}g F
                                {meal.prep_time_minutes > 0 && ` · ${meal.prep_time_minutes}m prep`}
                              </div>
                            </button>
                            <button
                              onClick={() => toggleLock(day.dayIndex, slot, snackIndex)}
                              className={`p-1.5 rounded-md transition-colors flex-shrink-0 ${
                                locked
                                  ? "text-[#ff7c0a] bg-[#ff7c0a]/10"
                                  : "text-slate-600 hover:text-slate-400"
                              }`}
                              title={locked ? "Unlock" : "Lock this meal"}
                            >
                              <LockIcon className="w-3.5 h-3.5" />
                            </button>
                          </div>

                          {/* Tags */}
                          {meal.tags && meal.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 px-3 pb-2">
                              {meal.tags.map((tag) => (
                                <span key={tag} className={TAG_COLORS[tag] || "badge-blue"}>
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}

                          {/* Expanded recipe detail */}
                          {expanded && (
                            <div className="border-t border-[#1f2a3c] px-3 py-3 space-y-3 animate-fade-in">
                              {meal.ingredients && meal.ingredients.length > 0 && (
                                <div>
                                  <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
                                    Ingredients
                                  </div>
                                  <ul className="space-y-1">
                                    {meal.ingredients.map((ing, i) => (
                                      <li key={i} className="flex justify-between text-xs">
                                        <span className="text-slate-300">{ing.name}</span>
                                        <span className="text-slate-500 ml-3 text-right">{ing.quantity}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {meal.instructions && meal.instructions.length > 0 && (
                                <div>
                                  <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
                                    Instructions
                                  </div>
                                  <ol className="space-y-1.5 list-none">
                                    {meal.instructions.map((step, i) => (
                                      <li key={i} className="flex gap-2 text-xs text-slate-400">
                                        <span className="text-[#ff7c0a] font-semibold flex-shrink-0 w-4">
                                          {i + 1}.
                                        </span>
                                        <span>{step}</span>
                                      </li>
                                    ))}
                                  </ol>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Day macro totals */}
                    <div className="flex justify-between items-center pt-2 border-t border-[#1f2a3c] text-xs">
                      <div className="flex gap-4 text-slate-500">
                        <span>{totals.calories} kcal</span>
                        <span className="text-[#ff7c0a] font-medium">{totals.protein}g P</span>
                        <span>{totals.carbs}g C</span>
                        <span>{totals.fat}g F</span>
                      </div>
                      {profile && (
                        <div className={`text-xs font-semibold ${
                          totals.calories <= profile.calorie_target * 1.05
                            ? "text-emerald-400" : "text-yellow-400"
                        }`}>
                          {totals.calories <= profile.calorie_target * 1.05 ? "✓ On target" : "⚠ Over target"}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Batch cook callout ────────────────────────────────────────── */}
      {plan && (
        <div className="card border-blue-500/20 bg-blue-950/20">
          <div className="flex items-start gap-3">
            <span className="text-xl flex-shrink-0">🍳</span>
            <div>
              <div className="text-sm font-semibold text-blue-300 mb-1">Batch Cook Tip</div>
              <div className="text-xs text-slate-400">
                Meals tagged <span className="badge-blue mx-1">batch-cook</span> are designed to
                make multiple servings at once. Cook on Sunday to cover several weekday meals —
                saves time and keeps you on track.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Skeletons + empty ───────────────────────────────────────────────────────

function PlanSkeleton() {
  return (
    <div className="space-y-3 animate-pulse">
      {Array.from({ length: 7 }, (_, i) => (
        <div key={i} className="h-16 bg-[#161d28] rounded-xl" />
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="card text-center py-12 text-slate-500">
      <span className="text-4xl block mb-3">📅</span>
      <p className="text-sm">No meal plan generated yet.</p>
      <p className="text-xs mt-1">Complete onboarding to generate your first plan.</p>
    </div>
  );
}

// ─── Icons ───────────────────────────────────────────────────────────────────
function RefreshIcon({ className }) {
  return <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
  </svg>;
}
function BagIcon({ className }) {
  return <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
      d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/>
  </svg>;
}
function LockIcon({ className }) {
  return <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <rect x="5" y="11" width="14" height="10" rx="2" strokeWidth="2"/>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
      d="M8 11V7a4 4 0 018 0v4"/>
  </svg>;
}
function ChevronIcon({ className }) {
  return <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
  </svg>;
}
